import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { bimSetActKC2OtherAcceptedCostGql, bimSetActKC2OtherPlanCostGql } from '../bimWorkOrderOthersQueries';

interface IUseBimSetActKC2OtherCost {
    workOrderOtherPriceId: number;
    actKC2OtherId: number;
    isPlanCost: boolean;
    onUpdateFinish: (isSuccess: boolean) => void;
    onUpdateStart: () => void;
}

export const useBimSetActKC2OtherCost = (props: IUseBimSetActKC2OtherCost) => {
    const dispatch = useDispatch();

    const { workOrderOtherPriceId, actKC2OtherId, isPlanCost, onUpdateFinish, onUpdateStart } = props;

    const requestId = `bimSetActKC2OtherCost_${workOrderOtherPriceId}_${actKC2OtherId}_${
        isPlanCost ? 'plan' : 'accepted'
    }`;

    const [bimSetActKC2OtherCostReset] = useResetStoreData({ requestId });

    const onSuccessLocal = useCallback(() => {
        bimSetActKC2OtherCostReset(undefined);

        onUpdateFinish(true);
    }, [bimSetActKC2OtherCostReset, onUpdateFinish]);

    const [
        bimSetActKC2OtherPlanCostData,
        bimSetActKC2OtherPlanCostStatus,
        ,
        bimSetActKC2OtherPlanCostRequest,
        ,
        bimSetActKC2OtherPlanCostAbortRequest,
    ] = useRequest({
        requestId,
        withEffect: false,
        query: bimSetActKC2OtherPlanCostGql,
        dependenciesUpdater: onSuccessLocal,
    });

    const [
        bimSetActKC2OtherAcceptedCostData,
        bimSetActKC2OtherAcceptedCostStatus,
        ,
        bimSetActKC2OtherAcceptedCostRequest,
        ,
        bimSetActKC2OtherAcceptedCostAbortRequest,
    ] = useRequest({
        requestId,
        withEffect: false,
        query: bimSetActKC2OtherAcceptedCostGql,
        dependenciesUpdater: onSuccessLocal,
    });

    useEffect(() => {
        bimSetActKC2OtherCostReset(undefined);
    }, [bimSetActKC2OtherCostReset]);

    useEffect(() => {
        if (
            (isPlanCost && bimSetActKC2OtherPlanCostStatus === RequestStatus.ERROR) ||
            (bimSetActKC2OtherPlanCostStatus === RequestStatus.SUCCESS &&
                !bimSetActKC2OtherPlanCostData?.bimSetActKC2OtherPlanCost?.result)
        ) {
            onUpdateFinish(false);
        }

        if (
            (!isPlanCost && bimSetActKC2OtherAcceptedCostStatus === RequestStatus.ERROR) ||
            (bimSetActKC2OtherAcceptedCostStatus === RequestStatus.SUCCESS &&
                !bimSetActKC2OtherAcceptedCostData?.bimSetActKC2OtherAcceptedCost?.result)
        ) {
            onUpdateFinish(false);
        }
    }, [
        bimSetActKC2OtherAcceptedCostData?.bimSetActKC2OtherAcceptedCost?.result,
        bimSetActKC2OtherAcceptedCostStatus,
        bimSetActKC2OtherPlanCostData?.bimSetActKC2OtherPlanCost?.result,
        bimSetActKC2OtherPlanCostStatus,
        isPlanCost,
        onUpdateFinish,
    ]);

    const bimSetActKC2OtherCostRequest = useCallback(
        (cost: number) => {
            const params = { workOrderOtherPriceId, actKC2OtherId, cost };

            onUpdateStart();

            if (isPlanCost) {
                if (bimSetActKC2OtherPlanCostStatus === RequestStatus.FETCHING) {
                    dispatch(bimSetActKC2OtherPlanCostAbortRequest(undefined));
                }

                dispatch(bimSetActKC2OtherPlanCostRequest(params));
            } else {
                if (bimSetActKC2OtherAcceptedCostStatus === RequestStatus.FETCHING) {
                    dispatch(bimSetActKC2OtherAcceptedCostAbortRequest(undefined));
                }

                dispatch(bimSetActKC2OtherAcceptedCostRequest(params));
            }
        },
        [
            actKC2OtherId,
            bimSetActKC2OtherAcceptedCostAbortRequest,
            bimSetActKC2OtherAcceptedCostRequest,
            bimSetActKC2OtherAcceptedCostStatus,
            bimSetActKC2OtherPlanCostAbortRequest,
            bimSetActKC2OtherPlanCostRequest,
            bimSetActKC2OtherPlanCostStatus,
            dispatch,
            isPlanCost,
            onUpdateStart,
            workOrderOtherPriceId,
        ],
    );

    return {
        bimSetActKC2OtherCostRequest,
        bimSetActKC2OtherPlanCostStatus,
    };
};
